// Features1.js - Contains functions for creating a modern features section
import { v4 as uuidv4 } from 'uuid';
import { DEVICES } from '../../constants/breakpoints';

/**
 * Creates a Features section with three feature cards
 * @param {function} getHierarchyName - Function to get hierarchy name for elements
 * @param {string} currentPageId - The current page ID where the features section is being added
 * @param {function} setClassStylesRegistry - Function to update class styles registry
 * @param {function} setGlobalClassNames - Function to update global class names
 * @returns {Object} - The features section elements
 */
export const createFeaturesElements = (
  getHierarchyName, 
  currentPageId,
  setClassStylesRegistry,
  setGlobalClassNames
) => {
  // Define class names
  const featuresSectionClass = 'features1-section';
  const featuresContainerClass = 'features1-container';
  const featuresHeaderClass = 'features1-header';
  const featuresTitleClass = 'features1-title';
  const featuresSubtitleClass = 'features1-subtitle';
  const featuresCardsClass = 'features1-cards';
  const featureCardClass = 'features1-card';
  const featureIconClass = 'features1-icon';
  const featureCardTitleClass = 'features1-card-title';
  const featureCardDescriptionClass = 'features1-card-description';
  const featureLearnMoreClass = 'features1-learn-more';
  
  // Define styles
  // Desktop Styles (default)
  const featuresSectionStyles = {
    position: 'relative',
    paddingTop: '80px',
    paddingBottom: '80px',
    paddingLeft: '20px',
    paddingRight: '20px',
    backgroundColor: '#f8f9fa',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column'
  };

  const featuresContainerStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    maxWidth: '1200px',
    marginLeft: 'auto',
    marginRight: 'auto'
  };

  const featuresHeaderStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    marginBottom: '60px',
    maxWidth: '700px'
  };

  const featuresTitleStyles = {
    fontSize: '42px',
    fontWeight: '700',
    marginBottom: '16px',
    color: '#333333'
  };

  const featuresSubtitleStyles = {
    fontSize: '18px',
    lineHeight: '1.6',
    color: '#666666',
    maxWidth: '600px'
  };

  const featuresCardsStyles = {
    display: 'flex',
    justifyContent: 'center',
    gap: '30px',
    width: '100%'
  };

  const featureCardStyles = {
    display: 'flex',
    flexDirection: 'column',
    padding: '40px 30px',
    borderRadius: '10px',
    backgroundColor: '#ffffff',
    boxShadow: '0px 4px 20px rgba(0, 0, 0, 0.05)',
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    width: '100%',
    maxWidth: '380px',
    border: '1px solid #f0f0f0',
    alignItems: 'flex-start',
    '&:hover': {
      transform: 'translateY(-10px)',
      boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)'
    }
  };

  const featureIconStyles = {
    width: '60px',
    height: '60px',
    marginBottom: '20px',
    padding: '12px',
    borderRadius: '50%',
    backgroundColor: '#f0f0ff',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const featureCardTitleStyles = {
    fontSize: '22px',
    fontWeight: '600',
    marginBottom: '16px',
    color: '#333333'
  };

  const featureCardDescriptionStyles = {
    fontSize: '16px',
    lineHeight: '1.6',
    color: '#666666',
    marginBottom: '24px'
  };

  const featureLearnMoreStyles = {
    fontSize: '16px',
    fontWeight: '600',
    color: '#5b42b5',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    cursor: 'pointer',
    textDecoration: 'none',
    marginTop: 'auto'
  };

  // Tablet Styles
  const tabletFeaturesCardsStyles = {
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: '20px'
  };

  const tabletFeatureCardStyles = {
    maxWidth: '320px'
  };

  // Mobile Styles
  const mobileFeaturesCardsStyles = {
    flexDirection: 'column',
    alignItems: 'center',
    gap: '20px'
  };

  const mobileFeatureCardStyles = {
    maxWidth: '100%'
  };

  const mobileFeaturesTitleStyles = {
    fontSize: '32px'
  };

  // Update the classStylesRegistry
  setClassStylesRegistry(prev => {
    const newRegistry = { ...prev };
    
    // Desktop styles
    newRegistry[DEVICES.DESKTOP].set(featuresSectionClass, featuresSectionStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresContainerClass, featuresContainerStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresHeaderClass, featuresHeaderStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresTitleClass, featuresTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresSubtitleClass, featuresSubtitleStyles);
    newRegistry[DEVICES.DESKTOP].set(featuresCardsClass, featuresCardsStyles);
    newRegistry[DEVICES.DESKTOP].set(featureCardClass, featureCardStyles);
    newRegistry[DEVICES.DESKTOP].set(featureIconClass, featureIconStyles);
    newRegistry[DEVICES.DESKTOP].set(featureCardTitleClass, featureCardTitleStyles);
    newRegistry[DEVICES.DESKTOP].set(featureCardDescriptionClass, featureCardDescriptionStyles);
    newRegistry[DEVICES.DESKTOP].set(featureLearnMoreClass, featureLearnMoreStyles);

    // Tablet styles
    newRegistry[DEVICES.TABLET].set(featuresCardsClass, tabletFeaturesCardsStyles);
    newRegistry[DEVICES.TABLET].set(featureCardClass, tabletFeatureCardStyles);

    // Mobile styles
    newRegistry[DEVICES.MOBILE].set(featuresCardsClass, mobileFeaturesCardsStyles);
    newRegistry[DEVICES.MOBILE].set(featureCardClass, mobileFeatureCardStyles);
    newRegistry[DEVICES.MOBILE].set(featuresTitleClass, mobileFeaturesTitleStyles);

    return newRegistry;
  });

  // Add classes to globalClassNames
  setGlobalClassNames(prev => new Set([
    ...prev,
    featuresSectionClass,
    featuresContainerClass,
    featuresHeaderClass,
    featuresTitleClass,
    featuresSubtitleClass,
    featuresCardsClass,
    featureCardClass,
    featureIconClass,
    featureCardTitleClass,
    featureCardDescriptionClass,
    featureLearnMoreClass
  ]));

  // Generate IDs for elements
  const sectionId = Date.now();
  const containerId = Date.now() + 1;
  const headerId = Date.now() + 2;
  const titleId = Date.now() + 3;
  const subtitleId = Date.now() + 4;
  const cardsContainerId = Date.now() + 5;
  
  // Card 1 elements
  const card1Id = Date.now() + 6;
  const icon1Id = Date.now() + 7;
  const cardTitle1Id = Date.now() + 8;
  const cardDescription1Id = Date.now() + 9;
  const learnMore1Id = Date.now() + 10;
  
  // Card 2 elements
  const card2Id = Date.now() + 11;
  const icon2Id = Date.now() + 12;
  const cardTitle2Id = Date.now() + 13;
  const cardDescription2Id = Date.now() + 14;
  const learnMore2Id = Date.now() + 15;
  
  // Card 3 elements
  const card3Id = Date.now() + 16;
  const icon3Id = Date.now() + 17;
  const cardTitle3Id = Date.now() + 18;
  const cardDescription3Id = Date.now() + 19;
  const learnMore3Id = Date.now() + 20;

  const newElements = [
    // Main section
    {
      id: sectionId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'section',
      name: 'Features Section',
      parentId: null,
      className: featuresSectionClass,
      styles: {},
      children: [containerId],
      text: '',
      attributes: {
        'data-element': 'features-section',
        'data-component': 'section',
        'data-section-type': 'features',
        'data-page-id': currentPageId
      }
    },
    
    // Container
    {
      id: containerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Features Container',
      parentId: sectionId,
      hierarchyName: getHierarchyName('div'),
      className: featuresContainerClass,
      styles: {},
      children: [headerId, cardsContainerId],
      text: '',
      attributes: {
        'data-element': 'features-container'
      }
    },
    
    // Header
    {
      id: headerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Features Header',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: featuresHeaderClass,
      styles: {},
      children: [titleId, subtitleId],
      text: '',
      attributes: {
        'data-element': 'features-header'
      }
    },
    
    // Title
    {
      id: titleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h2',
      name: 'Features Title',
      parentId: headerId,
      hierarchyName: getHierarchyName('h2'),
      className: featuresTitleClass,
      styles: {},
      children: [],
      text: 'Key Features That Set Us Apart',
      attributes: {
        'data-element': 'features-title'
      }
    },
    
    // Subtitle
    {
      id: subtitleId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Features Subtitle',
      parentId: headerId,
      hierarchyName: getHierarchyName('p'),
      className: featuresSubtitleClass,
      styles: {},
      children: [],
      text: 'Explore our powerful features designed to help your business grow and thrive in today\'s competitive landscape.',
      attributes: {
        'data-element': 'features-subtitle'
      }
    },
    
    // Cards container
    {
      id: cardsContainerId,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Features Cards',
      parentId: containerId,
      hierarchyName: getHierarchyName('div'),
      className: featuresCardsClass,
      styles: {},
      children: [card1Id, card2Id, card3Id],
      text: '',
      attributes: {
        'data-element': 'features-cards'
      }
    },
    
    // Card 1
    {
      id: card1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Feature Card 1',
      parentId: cardsContainerId,
      hierarchyName: getHierarchyName('div'),
      className: featureCardClass,
      styles: {},
      children: [icon1Id, cardTitle1Id, cardDescription1Id, learnMore1Id],
      text: '',
      attributes: {
        'data-element': 'feature-card',
        'data-card': 'feature-1'
      }
    },
    
    // Card 1 Icon
    {
      id: icon1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Feature Icon 1',
      parentId: card1Id,
      hierarchyName: getHierarchyName('image'),
      className: featureIconClass,
      styles: {},
      src: 'https://api.iconify.design/material-symbols:dashboard-customize-outline-rounded.svg?color=%235b42b5&width=36&height=36',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Customization Feature'
      }
    },
    
    // Card 1 Title
    {
      id: cardTitle1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Feature Title 1',
      parentId: card1Id,
      hierarchyName: getHierarchyName('h3'),
      className: featureCardTitleClass,
      styles: {},
      children: [],
      text: 'Easy Customization',
      attributes: {
        'data-element': 'feature-title'
      }
    },
    
    // Card 1 Description
    {
      id: cardDescription1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Feature Description 1',
      parentId: card1Id,
      hierarchyName: getHierarchyName('p'),
      className: featureCardDescriptionClass,
      styles: {},
      children: [],
      text: 'Our intuitive interface allows you to customize every aspect of your website without any coding knowledge required.',
      attributes: {
        'data-element': 'feature-description'
      }
    },
    
    // Card 1 Learn More
    {
      id: learnMore1Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Learn More 1',
      parentId: card1Id,
      hierarchyName: getHierarchyName('link'),
      className: featureLearnMoreClass,
      styles: {},
      children: [],
      text: 'Learn more →',
      attributes: {
        'data-element': 'feature-link',
        'href': '#'
      }
    },
    
    // Card 2
    {
      id: card2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Feature Card 2',
      parentId: cardsContainerId,
      hierarchyName: getHierarchyName('div'),
      className: featureCardClass,
      styles: {},
      children: [icon2Id, cardTitle2Id, cardDescription2Id, learnMore2Id],
      text: '',
      attributes: {
        'data-element': 'feature-card',
        'data-card': 'feature-2'
      }
    },
    
    // Card 2 Icon
    {
      id: icon2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Feature Icon 2',
      parentId: card2Id,
      hierarchyName: getHierarchyName('image'),
      className: featureIconClass,
      styles: {
        backgroundColor: '#f0fff0'
      },
      src: 'https://api.iconify.design/material-symbols:speed-outline-rounded.svg?color=%235b42b5&width=36&height=36',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Performance Feature'
      }
    },
    
    // Card 2 Title
    {
      id: cardTitle2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Feature Title 2',
      parentId: card2Id,
      hierarchyName: getHierarchyName('h3'),
      className: featureCardTitleClass,
      styles: {},
      children: [],
      text: 'Lightning Fast',
      attributes: {
        'data-element': 'feature-title'
      }
    },
    
    // Card 2 Description
    {
      id: cardDescription2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Feature Description 2',
      parentId: card2Id,
      hierarchyName: getHierarchyName('p'),
      className: featureCardDescriptionClass,
      styles: {},
      children: [],
      text: 'Optimized for speed, our platform ensures your website loads quickly and performs smoothly on all devices.',
      attributes: {
        'data-element': 'feature-description'
      }
    },
    
    // Card 2 Learn More
    {
      id: learnMore2Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Learn More 2',
      parentId: card2Id,
      hierarchyName: getHierarchyName('link'),
      className: featureLearnMoreClass,
      styles: {},
      children: [],
      text: 'Learn more →',
      attributes: {
        'data-element': 'feature-link',
        'href': '#'
      }
    },
    
    // Card 3
    {
      id: card3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'div',
      name: 'Feature Card 3',
      parentId: cardsContainerId,
      hierarchyName: getHierarchyName('div'),
      className: featureCardClass,
      styles: {},
      children: [icon3Id, cardTitle3Id, cardDescription3Id, learnMore3Id],
      text: '',
      attributes: {
        'data-element': 'feature-card',
        'data-card': 'feature-3'
      }
    },
    
    // Card 3 Icon
    {
      id: icon3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'image',
      name: 'Feature Icon 3',
      parentId: card3Id,
      hierarchyName: getHierarchyName('image'),
      className: featureIconClass,
      styles: {
        backgroundColor: '#fff0f0'
      },
      src: 'https://api.iconify.design/material-symbols:devices-outline-rounded.svg?color=%235b42b5&width=36&height=36',
      attributes: {
        'data-element': 'feature-icon',
        'alt': 'Responsive Feature'
      }
    },
    
    // Card 3 Title
    {
      id: cardTitle3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'h3',
      name: 'Feature Title 3',
      parentId: card3Id,
      hierarchyName: getHierarchyName('h3'),
      className: featureCardTitleClass,
      styles: {},
      children: [],
      text: 'Fully Responsive',
      attributes: {
        'data-element': 'feature-title'
      }
    },
    
    // Card 3 Description
    {
      id: cardDescription3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'p',
      name: 'Feature Description 3',
      parentId: card3Id,
      hierarchyName: getHierarchyName('p'),
      className: featureCardDescriptionClass,
      styles: {},
      children: [],
      text: 'Your website will look stunning on any device, from desktop computers to smartphones and tablets.',
      attributes: {
        'data-element': 'feature-description'
      }
    },
    
    // Card 3 Learn More
    {
      id: learnMore3Id,
      builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      type: 'link',
      name: 'Learn More 3',
      parentId: card3Id,
      hierarchyName: getHierarchyName('link'),
      className: featureLearnMoreClass,
      styles: {},
      children: [],
      text: 'Learn more →',
      attributes: {
        'data-element': 'feature-link',
        'href': '#'
      }
    }
  ];

  return {
    featuresSection: newElements[0],
    elements: newElements
  };
};

export default createFeaturesElements; 